/**
 * @module routes.Sessions.CoachDetail.useCoachDetail
 */
import { useEffect, useMemo, useState } from "react";

import { schedulingFetchGet } from "app/utils/request/scheduling/fetch";
import { LessonRateModel } from "app/models/scheduling/LessonRateModel";
import {
  Event,
  Package,
  Session,
  transformLessonRatesToSessions,
  transformProgramToEvent,
  transformStripeProductToPackage,
} from "app/components/PublicProfiles/services/api";

/** A facility uuid, as the public programmes index insists on being asked with. */
const isUuid = (value?: string): boolean =>
  typeof value === "string" &&
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);

interface CoachDetail {
  sessions: Session[];
  /**
   * The rates as the API returns them. The cards need `collectType` and
   * `availablePaymentMethods` to tell "Book Now" from "Book with Package" and
   * "Requires Subscription", and the transformed Session drops both.
   */
  lessonRates: LessonRateModel[];
  packages: Package[];
  events: Event[];
  /** The coach's own copy of the profile — name, credentials, timezone. */
  profile: any | null;
  loading: boolean;
}

/**
 * The three things a coach's page shows, from the three endpoints the public
 * coach profile already uses. They are public, so they answer the same way for a
 * signed-in athlete, which is what lets the in-app page reuse both the requests
 * and the components that render them.
 *
 * A failure in one section does not blank the others: each request falls back to
 * an empty list, so a coach with no packages still shows their sessions.
 */
const useCoachDetail = (
  coachExternalId: string | undefined,
  facilityId: string | undefined,
  /**
   * CFTB-3445: ask which of these packages the viewer already subscribes to.
   *
   * Only a signed-in athlete has subscriptions, and only the authenticated
   * `stripe_student_products` payload carries the flag — the public products
   * endpoint this page lists from answers the same for everyone, so it cannot.
   * Off for a visitor, whose request would only 401.
   */
  athleteSignedIn = false,
): CoachDetail => {
  const [sessions, setSessions] = useState<Session[]>([]);
  const [lessonRates, setLessonRates] = useState<LessonRateModel[]>([]);
  const [packages, setPackages] = useState<Package[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  /**
   * Kept apart from `events` rather than merged into it: the two requests race,
   * and the public one landing second would overwrite a merge made by the first.
   * They are combined where they are read instead.
   */
  const [ownEvents, setOwnEvents] = useState<Event[]>([]);
  const [profile, setProfile] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!coachExternalId) {
      setLoading(false);
      return undefined;
    }

    let active = true;
    setLoading(true);

    const coachRequest = schedulingFetchGet(
      `coach_profiles/${coachExternalId}/landing_page_coach`,
      {},
      {},
      undefined,
      true,
    ).catch(() => null);

    const packagesRequest = schedulingFetchGet(
      `stripe_products`,
      { coach_id: coachExternalId, include_offline: true },
      {},
      undefined,
      true,
    ).catch(() => []);

    Promise.all([coachRequest, packagesRequest])
      .then(([coachResponse, stripeProducts]: any[]) => {
        if (!active) {
          return undefined;
        }

        setProfile(coachResponse ?? null);
        const rates = coachResponse?.lessonRates || [];
        setLessonRates(
          rates.map((rate: any) => new LessonRateModel(rate)),
        );
        setSessions(transformLessonRatesToSessions(rates));
        setPackages(
          (stripeProducts || []).map((product: any) =>
            transformStripeProductToPackage(product),
          ),
        );
        /**
         * CFTB-3437: the public programmes index is `Facility.find_by!(uuid:
         * params[:facility_id], public_programs: true)` — it answers to a uuid and
         * 404s on anything else, which is how this section came to be empty on every
         * screen that had only the facility's external id to hand.
         *
         * So the uuid is what it is asked with: the caller's, when the caller has one,
         * and otherwise the coach's own — which also gives the page its clinics when it
         * is opened with no facility in the booking state at all, as it is from Home.
         */
        const facilityUuid = isUuid(facilityId)
          ? facilityId
          : coachResponse?.facility?.uuid || facilityId;

        if (!facilityUuid) {
          setEvents([]);
          return undefined;
        }

        // The public index is `where(privacy: "public")` — the clinics anyone
        // may see. A private one reaches this page through the effect below.
        return schedulingFetchGet(
          "/programs",
          {
            facility_id: facilityUuid,
            registration_status: "open_now_or_in_future",
          },
          {},
          undefined,
          true,
        )
          .catch(() => null)
          .then((programsResponse: any) => {
            if (!active) {
              return;
            }
            setEvents(
              (programsResponse?.data?.active || []).map((program: any) =>
                transformProgramToEvent(
                  program,
                  coachResponse?.facility?.name || "",
                ),
              ),
            );
          });
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [coachExternalId, facilityId]);

  const facilityName = profile?.facility?.name || "";

  /**
   * The clinics only this athlete may see.
   *
   * A private clinic never appears in the public index above, so an athlete
   * registered for one — who sees it on their own Programs page, which asks the
   * authenticated index — found "No available booking options" on the coach's
   * page. That index keeps what a student is entitled to: their own facilities'
   * programmes whatever the privacy, plus anything the household is registered
   * for.
   *
   * As well as the public list rather than instead of it: a signed-in athlete
   * looking at a coach from a facility they have no connection to would
   * otherwise lose that facility's public clinics, which the visitor beside
   * them still sees.
   *
   * Its own effect, so it runs when the signed-in athlete arrives — the user
   * lands in the store after the first render, and the fetch above does not
   * wait for them.
   */
  useEffect(() => {
    if (!coachExternalId || !athleteSignedIn) {
      return undefined;
    }

    let active = true;

    schedulingFetchGet(
      "/programs",
      {
        coach_profile_id: coachExternalId,
        registration_status: "open_now_or_in_future",
      },
      { version: 3 },
    )
      .catch(() => null)
      .then((response: any) => {
        if (!active) {
          return;
        }

        setOwnEvents(
          ((response?.data?.active as any[]) || []).map((program) =>
            transformProgramToEvent(program, facilityName),
          ),
        );
      });

    return () => {
      active = false;
    };
  }, [coachExternalId, athleteSignedIn, facilityName]);

  /**
   * CFTB-3445: which of this coach's products the athlete already subscribes to.
   *
   * Its own effect on purpose. The signed-in athlete arrives a render after the
   * page mounts (the store rehydrates), and adding that flag to the fetch above
   * made all three of its requests run a second time when it flipped. Kept apart,
   * a late flag costs one request and the sections do not reload.
   *
   * Read for its `subscribed` flags only — the payload drops offline packages and
   * scopes products differently, so the list itself stays the public one. A failure
   * leaves every card purchasable, as before; the backend still refuses the
   * duplicate.
   */
  const [subscribedProductIds, setSubscribedProductIds] = useState<Set<string>>(
    () => new Set(),
  );

  useEffect(() => {
    if (!coachExternalId || !athleteSignedIn) {
      setSubscribedProductIds(new Set());
      return undefined;
    }

    let active = true;

    schedulingFetchGet("stripe_student_products", { coach_id: coachExternalId })
      .then((studentProducts: any) => {
        if (!active) {
          return;
        }
        setSubscribedProductIds(
          new Set(
            (studentProducts || [])
              .filter((product: any) => product?.subscribed === true)
              .map((product: any) => product.stripeProdId),
          ),
        );
      })
      .catch(() => undefined);

    return () => {
      active = false;
    };
  }, [coachExternalId, athleteSignedIn]);

  const packagesWithSubscriptions = useMemo(
    () =>
      subscribedProductIds.size === 0
        ? packages
        : packages.map((pkg) =>
            pkg.stripeProdId && subscribedProductIds.has(pkg.stripeProdId)
              ? { ...pkg, subscribed: true }
              : pkg,
          ),
    [packages, subscribedProductIds],
  );

  /**
   * The public clinics and the ones only this athlete may see, as one list.
   * Both can name the same clinic, so the id decides and the public copy wins.
   */
  const allEvents = useMemo(() => {
    if (ownEvents.length === 0) {
      return events;
    }

    const seen = new Set(events.map((event) => event.id));
    return [...events, ...ownEvents.filter((event) => !seen.has(event.id))];
  }, [events, ownEvents]);

  return {
    sessions,
    lessonRates,
    packages: packagesWithSubscriptions,
    events: allEvents,
    profile,
    loading,
  };
};

export default useCoachDetail;
